import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, Stack, Text, Title} from '@mantine/core';
import React from 'react';

import CanvasPreviewFrame from 'canvas-core/preview/CanvasPreviewFrame';
import useCanvasExpandedPreviewUrl from 'canvas-core/preview/useCanvasExpandedPreviewUrl';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {CenteredSpinner} from 'ui-library/Spinner';

import {
  AgentFactoryQuery as AgentFactoryQueryData,
  AgentFactoryQueryVariables,
} from './__generated/AgentFactoryQuery';

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing.md,
    height: '100%',
    minHeight: 0,
    padding: theme.spacing.xl,
  },
  canvasSection: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
  },
}));

export const AGENT_FACTORY_QUERY = gql`
  query AgentFactoryQuery($id: StrictString!) @engComponent(name: LLM) @skipAudit {
    agentFactory(id: $id) {
      __typename
      ... on AgentFactoryNode {
        id
        name
        description
        canvasVersion {
          id
        }
        createdAt
        creatorName
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * The canvas backing an agent factory, filling whatever height its container gives it. Reuses
 * useCanvasExpandedPreviewUrl and CanvasPreviewFrame -- the same signed-URL fetch and
 * iframe/sandbox setup chat-embedded canvases use (see ChatCanvasArtifactView) -- but renders only
 * the canvas contents, without any of that view's card chrome (icon tile, version badge, refresh
 * button), since this page already has its own header.
 */
function AgentFactoryCanvas({canvasVersionId}: {canvasVersionId: string | null}): JSX.Element {
  if (canvasVersionId == null) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text="This factory has not generated a canvas yet."
        {...getTestProps({component: 'AgentFactoryPage', element: 'noCanvasBanner'})}
      />
    );
  }
  return <AgentFactoryCanvasPreview canvasVersionId={canvasVersionId} />;
}

function AgentFactoryCanvasPreview({canvasVersionId}: {canvasVersionId: string}): JSX.Element {
  const {signedUrl, isLoading, errorMessage} = useCanvasExpandedPreviewUrl({canvasVersionId});

  if (isLoading) {
    return <CenteredSpinner />;
  }

  if (errorMessage != null) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />;
  }

  if (signedUrl == null) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load the canvas." />;
  }

  return <CanvasPreviewFrame signedUrl={signedUrl} />;
}

/**
 * Detail view for a single Agent Factory, rendered as its own workspace tab (see BufferUtil and
 * EditorRoutes.agentFactoryEditor) and addressable at its own URL (see AIRoutes): the factory's
 * name and description, plus -- filling the remaining height -- the canvas currently backing it, if
 * one has been generated yet.
 */
export default function AgentFactoryPage({agentFactoryId}: {agentFactoryId: string}): JSX.Element {
  const {classes} = useStyles();
  const queryResult = useBenchlingQuery<AgentFactoryQueryData, AgentFactoryQueryVariables>(
    AGENT_FACTORY_QUERY,
    {variables: {id: agentFactoryId}}
  );

  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  if (
    queryResult.type === QueryResultType.ERROR ||
    queryResult.data.agentFactory == null ||
    isInaccessible(queryResult.data.agentFactory)
  ) {
    return (
      <Stack spacing="md" className={classes.container}>
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="This factory could not be found, or you do not have access to it."
        />
      </Stack>
    );
  }

  const {agentFactory} = queryResult.data;

  return (
    <div className={classes.container} {...getTestProps({component: 'AgentFactoryPage', element: 'root'})}>
      <Title order={3}>{agentFactory.name}</Title>
      <Text c="dimmed">{agentFactory.description || 'No description yet.'}</Text>
      <div className={classes.canvasSection}>
        <AgentFactoryCanvas canvasVersionId={agentFactory.canvasVersion?.id ?? null} />
      </div>
    </div>
  );
}
